import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  ArrowLeft, Trash2, X, Send, Paperclip, CheckCircle2, Pencil, FileText, DollarSign,
} from "lucide-react";
import { TICKET_CATEGORIES, PriorityBangs, CategoryPill } from "../components/TicketCard";
import TicketModal from "../components/TicketModal";
import { useAuth } from "../lib/auth";

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const readOnly = user?.role === "viewer";
  const canMutate = !readOnly;
  const [ticket, setTicket] = useState(null);
  const [editing, setEditing] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setTicket(data);
    } catch {
      toast.error("Ticket not found");
      navigate(-1);
    }
  };
  useEffect(() => { load(); }, [id]);

  if (!ticket) return <div className="text-sm text-zinc-500" data-testid="ticket-detail-loading">Loading…</div>;
  const isComplete = ticket.status === "complete";

  const toggleComplete = async () => {
    if (!isAdmin) {
      toast.error("Only admin can mark complete");
      return;
    }
    try {
      const { data } = await api.put(`/tickets/${id}`, { status: isComplete ? "active" : "complete" });
      setTicket(data);
      toast.success(isComplete ? "Reopened" : "Marked complete");
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this ticket?")) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Deleted");
      navigate(isAdmin ? `/work-orders/${ticket.client_id}` : "/tickets");
    } catch {
      toast.error("Failed");
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setPosting(true);
    try {
      await api.post(`/tickets/${id}/notes`, { body: noteBody.trim() });
      setNoteBody("");
      load();
    } catch {
      toast.error("Failed to post note");
    } finally {
      setPosting(false);
    }
  };

  const removeNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/tickets/${id}/notes/${noteId}`);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const savePrice = async () => {
    let val = null;
    if (priceInput === "" || priceInput === "no-cost") {
      val = priceInput === "no-cost" ? 0 : null;
    } else {
      const num = parseFloat(priceInput);
      if (!isNaN(num) && num >= 0) val = num;
    }
    try {
      const { data } = await api.put(`/tickets/${id}`, { price_amount: val });
      setTicket(data);
      setEditingPrice(false);
      toast.success("Price updated");
    } catch {
      toast.error("Failed to update price");
    }
  };

  const cat = TICKET_CATEGORIES[ticket.category] || TICKET_CATEGORIES.other;

  return (
    <div data-testid="ticket-detail-page" className={isComplete ? "" : ""}>
      <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <CategoryPill category={ticket.category} />
            <PriorityBangs level={ticket.priority} />
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${isComplete ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-700"}`}>
              {isComplete ? "Complete" : "Active"}
            </span>
          </div>
          <h1 data-testid="ticket-detail-title" className={`mt-3 text-3xl font-semibold tracking-tight text-zinc-900 ${isComplete ? "opacity-60 grayscale" : ""}`}>{ticket.title}</h1>
          <div className="mt-1 text-xs text-zinc-500">
            Created {ticket.created_at ? format(parseISO(ticket.created_at), "MMM d, yyyy · p") : "—"}
            {ticket.page_name && <> · on <span className="font-medium text-zinc-700">{ticket.page_name}</span></>}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {canMutate && (
            <button data-testid="ticket-edit" onClick={() => setEditing(true)} className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
              <Pencil size={14} /> Edit
            </button>
          )}
          {isAdmin && (
            <button
              data-testid="ticket-toggle-complete"
              onClick={toggleComplete}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                isComplete ? "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50" : "bg-emerald-500 text-white hover:bg-emerald-600"
              }`}
            >
              <CheckCircle2 size={14} /> {isComplete ? "Reopen" : "Mark Complete"}
            </button>
          )}
          {canMutate && (
            <button data-testid="ticket-delete" onClick={remove} className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Description */}
          <div className="rounded-xl border border-zinc-200 bg-white p-6">
            <h2 className="text-sm font-semibold tracking-tight text-zinc-900">Description</h2>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-zinc-700">
              {ticket.description || <span className="text-zinc-400">No description.</span>}
            </p>
            {ticket.attachment_file_id && (
              <a
                href={`${API_BASE}/files/${ticket.attachment_file_id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ticket-attachment-link"
                className="mt-4 inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs font-medium text-zinc-700 hover:border-[#0066ff] hover:text-[#0066ff]"
              >
                <Paperclip size={12} /> {ticket.attachment_name || "Attachment"}
              </a>
            )}
          </div>

          {/* Conversation Notes */}
          <div className="rounded-xl border border-zinc-200 bg-white p-6">
            <h2 className="text-sm font-semibold tracking-tight text-zinc-900">Notes & Conversation</h2>
            <p className="mt-1 text-xs text-zinc-500">Each note is dated and labeled by author.</p>
            <div className="mt-4 flex flex-col gap-3">
              {(ticket.notes || []).length === 0 && (
                <div className="rounded-md border border-dashed border-zinc-200 p-4 text-center text-xs text-zinc-400">No notes yet.</div>
              )}
              {(ticket.notes || []).map((n) => {
                const mine = n.author_id === user?.user_id;
                return (
                  <div
                    key={n.id}
                    data-testid={`ticket-note-${n.id}`}
                    className={`group rounded-lg border p-3 ${mine ? "border-[#0066ff]/30 bg-[#E5F0FF]/40" : "border-zinc-200 bg-zinc-50"}`}
                  >
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${n.author_role === "admin" ? "bg-[#0066ff] text-white" : "bg-zinc-200 text-zinc-800"}`}>
                          {n.author_role === "admin" ? "DeepThree" : "Client"}
                        </span>
                        <span className="text-zinc-700 font-medium">{n.author_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-400">
                        <span>{n.created_at ? format(parseISO(n.created_at), "MMM d, p") : ""}</span>
                        {mine && (
                          <button onClick={() => removeNote(n.id)} className="opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-600">
                            <X size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-800">{n.body}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex gap-2">
              <textarea
                data-testid="ticket-new-note"
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                disabled={readOnly}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote();
                }}
                placeholder={readOnly ? "Read-only" : "Write a note… (⌘+Enter to send)"}
                rows={2}
                className="flex-1 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20 disabled:bg-zinc-50"
              />
              {!readOnly && (
                <button
                  data-testid="ticket-post-note"
                  onClick={addNote}
                  disabled={posting || !noteBody.trim()}
                  className="inline-flex items-center gap-2 rounded-md bg-[#0066ff] px-3 py-2 text-sm font-medium text-white hover:bg-[#0052cc] disabled:opacity-50"
                >
                  <Send size={14} /> {posting ? "Posting…" : "Send"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Details</div>
            <dl className="mt-4 flex flex-col gap-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-zinc-500">Status</dt>
                <dd className={`font-medium ${isComplete ? "text-emerald-700" : "text-zinc-900"}`}>{isComplete ? "Complete" : "Active"}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-zinc-500">Priority</dt>
                <dd><PriorityBangs level={ticket.priority} /></dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-zinc-500">Category</dt>
                <dd className="font-medium" style={{ color: cat.color }}>{cat.label}</dd>
              </div>
              {ticket.page_name && (
                <div className="flex items-center justify-between">
                  <dt className="text-zinc-500">Page</dt>
                  <dd className="font-medium text-zinc-900">{ticket.page_name}</dd>
                </div>
              )}
              {ticket.completed_at && (
                <div className="flex items-center justify-between">
                  <dt className="text-zinc-500">Completed</dt>
                  <dd className="font-medium text-zinc-900">{format(parseISO(ticket.completed_at), "MMM d, p")}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Price (admin only edit) */}
          <div className="rounded-xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">
                <DollarSign size={12} /> Price
              </div>
              {isAdmin && !editingPrice && (
                <button data-testid="ticket-edit-price" onClick={() => { setEditingPrice(true); setPriceInput(typeof ticket.price_amount === "number" ? (ticket.price_amount === 0 ? "no-cost" : String(ticket.price_amount)) : ""); }} className="text-xs font-medium text-[#0066ff] hover:underline">Edit</button>
              )}
            </div>
            {!editingPrice ? (
              <div data-testid="ticket-price-display" className="mt-2 text-lg font-semibold text-zinc-900">
                {typeof ticket.price_amount === "number"
                  ? (ticket.price_amount === 0 ? "No cost" : `$${ticket.price_amount.toFixed(2)}`)
                  : <span className="text-sm font-normal text-zinc-400">Not set</span>}
              </div>
            ) : (
              <div className="mt-3 flex flex-col gap-2">
                <select data-testid="ticket-price-type" value={priceInput === "no-cost" ? "no-cost" : (priceInput === "" ? "unset" : "amount")} onChange={(e) => {
                  if (e.target.value === "no-cost") setPriceInput("no-cost");
                  else if (e.target.value === "unset") setPriceInput("");
                  else setPriceInput(priceInput && priceInput !== "no-cost" ? priceInput : "0");
                }} className="rounded-md border border-zinc-200 px-2 py-1.5 text-sm">
                  <option value="unset">Not set</option>
                  <option value="no-cost">No cost</option>
                  <option value="amount">Dollar amount</option>
                </select>
                {priceInput !== "no-cost" && priceInput !== "" && (
                  <input data-testid="ticket-price-amount" type="number" min="0" step="0.01" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} className="rounded-md border border-zinc-200 px-2 py-1.5 text-sm" />
                )}
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEditingPrice(false)} className="rounded-md px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100">Cancel</button>
                  <button data-testid="ticket-price-save" onClick={savePrice} className="rounded-md bg-[#0066ff] px-3 py-1 text-xs font-medium text-white hover:bg-[#0052cc]">Save</button>
                </div>
              </div>
            )}
            {!isAdmin && typeof ticket.price_amount !== "number" && (
              <p className="mt-2 text-xs text-zinc-500">Price will be set by DeepThree.</p>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <TicketModal
          mode="edit"
          initial={ticket}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
    </div>
  );
}
